import { useAtomValue } from 'jotai';
import { useCallback, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import type { SettingChange, SettingChangeGroup } from '@/components/settings/settings-save.types';
import { useSettingsSave } from '@/components/settings/use-settings-save';
import {
  getUpdatedDefrostSchedule,
  getUpdatedSequencer,
  getUpdatedSettings,
} from '@/features/equipment-browser/equipment-details/details-form/equipment-details-form.helpers';
import type { DetailsFormData } from '@/features/equipment-browser/equipment-details/details-form/equipment-details-form.types';
import { getEquipmentDetailsSaveBarChangeCount } from '@/features/equipment-browser/equipment-details/details-form/equipment-details-form-save-count.helpers';
import {
  getBooleanSettingVocabulary,
  isBooleanSetting,
} from '@/features/equipment-browser/equipment-details/settings/components/setting-field-boolean.utils';
import { defrostScheduleValuesAtom } from '@/features/equipment-browser/equipment-details/state/defrost-schedule.state';
import { displaySettingsListAtom } from '@/features/equipment-browser/equipment-details/state/display-settings.state';
import { activeSequencerDisplaySettingsListAtom } from '@/features/equipment-browser/equipment-details/state/sequencers.state';
import type { EnhancedSetting } from '@/features/equipment-browser/subscriptions/use-get-settings-values';
import { getSourceUnits } from '@/features/equipment-browser/utils';
import { getCatalogUnitsLabel } from '@/helpers/unit-formatter/get-catalog-units-label';
import { NO_VALUE } from '@/helpers/unit-formatter/shared/constants';
import { formatUnits } from '@/helpers/unit-formatter/unit-formatter';
import { useUpdateSettings } from '@/hooks/query-hooks/settings';
import { useCurrentFacility } from '@/hooks/use-current-facility';

/**
 * Builds SettingChange[] from dirty scalar form fields for a given section.
 */
export const buildChangesForSection = (
  sectionKey: 'settings' | 'sequencerSettings',
  dirtyKeys: string[],
  formValues: DetailsFormData,
  settings: EnhancedSetting[]
): SettingChange[] => {
  if (dirtyKeys.length === 0) return [];

  // Extract only dirty values for getUpdatedSettings
  const dirtyValues: Record<string, string> = {};
  for (const key of dirtyKeys) {
    dirtyValues[key] = formValues[sectionKey]?.[key] ?? '';
  }

  const desiredValues = getUpdatedSettings(settings, dirtyValues);
  // Map from settingId to DesiredSettingValue for lookup
  const desiredBySettingId = new Map(desiredValues.map((dv) => [dv.settingId, dv]));
  // Same trick for the per-dirty-key setting lookup below — replaces the
  // O(N·D) inner `.find()` that ran every keystroke via `form.watch()`.
  const settingsById = new Map(settings.map((s) => [s.ref.id, s]));

  const changes: SettingChange[] = [];

  for (const key of dirtyKeys) {
    const setting = settingsById.get(key);
    if (!setting) continue;

    const desiredValue = desiredBySettingId.get(key);
    if (!desiredValue) continue;

    const displayUnits = setting.displayUnits;
    const sourceUnits = getSourceUnits(setting);
    const original = formatUnits({ value: setting.value, units: sourceUnits }, displayUnits);
    const currentFormValue = formValues[sectionKey]?.[key]?.toString() ?? '';

    // The setting's own vocabulary, so the dialog describes a change the same way
    // the field does. `getOnOffLabel` forced ON / OFF whatever the setting said,
    // which read "ON <- OPEN" on an open-closed valve; and it could not read its
    // own stored labels, so the current value fell through to the raw string.
    const booleanVocabulary = isBooleanSetting(setting)
      ? getBooleanSettingVocabulary(setting)
      : undefined;
    const currentBooleanLabel = booleanVocabulary?.fromStored(currentFormValue) || undefined;
    const originalBooleanLabel = booleanVocabulary?.fromStored(setting.value) || undefined;

    const displayCurrentValue = currentBooleanLabel ?? (currentFormValue.toUpperCase() || NO_VALUE);
    // Typed values are always in displayUnits; original keeps formatUnits' vacuum label.
    const displayCurrentUnits = currentBooleanLabel
      ? ''
      : getCatalogUnitsLabel(setting.displayUnits);
    const displayOriginal = originalBooleanLabel ?? original.valueUnitsLabel?.toUpperCase() ?? '';

    changes.push({
      changeId: `${sectionKey}-${key}`,
      settingId: key,
      label: setting.label ?? '',
      currentValue: formValues[sectionKey]?.[key]?.toString() ?? '',
      displayCurrent: `${displayCurrentValue} ${displayCurrentUnits}`.trim(),
      displayOriginal,
      displayCurrentValue,
      displayCurrentUnits,
      desiredValue,
      platformValidations: setting.ref.validations as unknown,
      metadata: { sectionKey, fieldId: key },
    });
  }

  return changes;
};

/**
 * useDetailsSettingsSave
 *
 * Adapter hook that bridges the equipment details form with the shared settings save layer.
 * Derives SettingChange[] from all dirty form sections — scalar settings, sequencer settings,
 * sequencer configuration tables, and defrost schedule — and saves them one-at-a-time via
 * the shared useSettingsSave hook.
 *
 * Scalar settings show field-level before/after diffs. Sequencer config and defrost schedule
 * use the SettingChange.message field to display a descriptive message instead.
 */
export const useDetailsSettingsSave = () => {
  const form = useFormContext<DetailsFormData>();
  const displaySettings = useAtomValue(displaySettingsListAtom);
  const activeSequencerDisplaySettings = useAtomValue(activeSequencerDisplaySettingsListAtom);
  const defrostScheduleValues = useAtomValue(defrostScheduleValuesAtom);

  const { organizationId: orgId, agentId } = useCurrentFacility();
  const { mutateAsync: updateSettings } = useUpdateSettings({
    orgId,
    agentId,
    disableDefaultCallbacks: true,
  });

  const { dirtyFields } = form.formState;
  // Subscribe to all form values so settingChanges recomputes when any value
  // changes — not just when the set of dirty keys changes. Without this,
  // editing a value (e.g. 0.9 → 2) wouldn't update displayCurrent/desiredValue
  // because the field stays dirty under the same key.
  const watchedValues = form.watch();

  // Build SettingChange[] reactively from ALL dirty form sections.
  const settingChanges = useMemo((): SettingChange[] => {
    const formValues = watchedValues;
    const changes: SettingChange[] = [];

    // ── Scalar settings ──
    const dirtySettingsKeys = Object.keys((dirtyFields?.settings as Record<string, unknown>) ?? {});
    changes.push(
      ...buildChangesForSection('settings', dirtySettingsKeys, formValues, displaySettings)
    );

    // ── Sequencer settings (scalar) ──
    const dirtySequencerSettingsKeys = Object.keys(
      (dirtyFields?.sequencerSettings as Record<string, unknown>) ?? {}
    );
    changes.push(
      ...buildChangesForSection(
        'sequencerSettings',
        dirtySequencerSettingsKeys,
        formValues,
        activeSequencerDisplaySettings
      )
    );

    // ── Sequencer config (table data — message-type preview) ──
    const dirtySequencerKeys = Object.keys(
      (dirtyFields?.sequencer as Record<string, unknown>) ?? {}
    );
    if (dirtySequencerKeys.length > 0) {
      const dirtySequencer: DetailsFormData['sequencer'] = {};
      for (const key of dirtySequencerKeys) {
        dirtySequencer[key] = formValues.sequencer?.[key];
      }
      const desiredValues = getUpdatedSequencer(dirtySequencer);
      for (const dv of desiredValues) {
        changes.push({
          changeId: `sequencer-${dv.settingId}`,
          settingId: dv.settingId,
          label: 'Sequencer Configuration',
          currentValue: '',
          displayCurrent: '',
          displayOriginal: '',
          desiredValue: dv,
          message:
            'You have made changes to the sequencer configuration. Please verify them on the previous screen before continuing.',
          metadata: { sectionKey: 'sequencer', fieldId: dv.settingId },
        });
      }
    }

    // ── Defrost schedule (message-type preview) ──
    const hasDefrostChanges = !!(
      dirtyFields?.defrostSchedule &&
      (Array.isArray(dirtyFields.defrostSchedule)
        ? dirtyFields.defrostSchedule.length > 0
        : dirtyFields.defrostSchedule)
    );
    if (hasDefrostChanges && defrostScheduleValues?.defrostSchedule?.settingId) {
      const dv = getUpdatedDefrostSchedule(
        defrostScheduleValues.defrostSchedule.settingId,
        formValues.defrostSchedule
      );
      changes.push({
        changeId: `defrostSchedule-${dv.settingId}`,
        settingId: dv.settingId,
        label: 'Defrost Schedule',
        currentValue: '',
        displayCurrent: '',
        displayOriginal: '',
        desiredValue: dv,
        message:
          'You have made changes to the defrost schedule. Please verify them on the previous screen before continuing.',
        metadata: { sectionKey: 'defrostSchedule', fieldId: dv.settingId },
      });
    }

    return changes;
  }, [
    watchedValues,
    dirtyFields,
    displaySettings,
    activeSequencerDisplaySettings,
    defrostScheduleValues,
  ]);

  // Build groups for the dialog
  const groups = useMemo((): SettingChangeGroup[] => {
    const result: SettingChangeGroup[] = [];

    const settingsGroup = settingChanges.filter(
      (c) => (c.metadata as { sectionKey: string })?.sectionKey === 'settings'
    );
    if (settingsGroup.length > 0) {
      result.push({ id: 'settings', label: 'Settings', changes: settingsGroup });
    }

    const sequencerSettingsGroup = settingChanges.filter(
      (c) => (c.metadata as { sectionKey: string })?.sectionKey === 'sequencerSettings'
    );
    if (sequencerSettingsGroup.length > 0) {
      result.push({
        id: 'sequencerSettings',
        label: 'Sequencer Settings',
        changes: sequencerSettingsGroup,
      });
    }

    const sequencerGroup = settingChanges.filter(
      (c) => (c.metadata as { sectionKey: string })?.sectionKey === 'sequencer'
    );
    if (sequencerGroup.length > 0) {
      result.push({ id: 'sequencer', label: 'Sequencer Configuration', changes: sequencerGroup });
    }

    const defrostGroup = settingChanges.filter(
      (c) => (c.metadata as { sectionKey: string })?.sectionKey === 'defrostSchedule'
    );
    if (defrostGroup.length > 0) {
      result.push({ id: 'defrostSchedule', label: 'Defrost Schedule', changes: defrostGroup });
    }

    return result;
  }, [settingChanges]);

  const handleSaveComplete = useCallback(
    (result: { completed: string[]; failed: string[]; cancelled: string[] }) => {
      const allCompleted =
        result.completed.length > 0 && result.failed.length === 0 && result.cancelled.length === 0;

      if (allCompleted) {
        // Reset without arguments so RHF re-syncs from the `values` prop,
        // which already has server-confirmed values via WebSocket → atoms.
        // Using form.reset(form.getValues()) would lock in the raw user input
        // (e.g. "-4") instead of the server's converted value (e.g. "8.14").
        form.reset();
      } else {
        // Partial success: reset only completed fields so they pick up
        // server-confirmed values from the `values` prop.
        for (const changeId of result.completed) {
          const change = settingChanges.find((c) => c.changeId === changeId);
          if (!change?.metadata) continue;
          const { sectionKey, fieldId } = change.metadata as {
            sectionKey: string;
            fieldId: string;
          };

          if (sectionKey === 'settings' || sectionKey === 'sequencerSettings') {
            form.resetField(`${sectionKey}.${fieldId}` as keyof DetailsFormData);
          } else if (sectionKey === 'sequencer') {
            form.resetField(`sequencer.${fieldId}` as keyof DetailsFormData);
          } else if (sectionKey === 'defrostSchedule') {
            form.resetField('defrostSchedule');
          }
        }
      }
    },
    [form, settingChanges]
  );

  const settingsSave = useSettingsSave({
    changes: settingChanges,
    updateFn: updateSettings,
    onSaveComplete: handleSaveComplete,
  });

  /** Discard a single change by resetting its form field */
  const handleDiscard = useCallback(
    (changeId: string) => {
      const change = settingChanges.find((c) => c.changeId === changeId);
      if (!change?.metadata) return;
      const { sectionKey, fieldId } = change.metadata as { sectionKey: string; fieldId: string };

      if (sectionKey === 'settings' || sectionKey === 'sequencerSettings') {
        form.resetField(`${sectionKey}.${fieldId}` as keyof DetailsFormData);
      } else if (sectionKey === 'sequencer') {
        form.resetField(`sequencer.${fieldId}` as keyof DetailsFormData);
      } else if (sectionKey === 'defrostSchedule') {
        form.resetField('defrostSchedule');
      }
    },
    [form, settingChanges]
  );

  // Wrap open to snapshot displayOriginal values at the moment the dialog opens,
  // preventing live WebSocket updates from changing them mid-save. We still use
  // live groups so discards are reflected immediately — only displayOriginal is frozen.
  const originalSnapshotRef = useRef<Map<string, string>>(new Map());

  const openDialog = useCallback(() => {
    const snapshot = new Map<string, string>();
    for (const group of groups) {
      for (const change of group.changes) {
        snapshot.set(change.changeId, change.displayOriginal);
      }
    }
    originalSnapshotRef.current = snapshot;
    settingsSave.open();
  }, [groups, settingsSave]);

  // Use live groups (so discards remove items) but overlay the snapshotted
  // displayOriginal values so WebSocket updates don't change them mid-save.
  const displayGroups = useMemo(() => {
    if (!settingsSave.isOpen || originalSnapshotRef.current.size === 0) return groups;
    return groups.map((group) => ({
      ...group,
      changes: group.changes.map((change) => ({
        ...change,
        displayOriginal: originalSnapshotRef.current.get(change.changeId) ?? change.displayOriginal,
      })),
    }));
  }, [groups, settingsSave.isOpen]);

  const hasChanges = settingChanges.length > 0;
  const changeCount = useMemo(
    () => getEquipmentDetailsSaveBarChangeCount(dirtyFields, settingChanges),
    [dirtyFields, settingChanges]
  );

  return {
    settingsSave,
    groups: displayGroups,
    hasChanges,
    changeCount,
    handleDiscard,
    openDialog,
  };
};
